import { FC, useContext } from 'react';

import { gql, useApolloClient, useMutation } from '@apollo/client';
import { Button, CheckIcon, Input, Modal, Select } from '@smartshell/ui-kit';
import { Controller, useForm } from 'react-hook-form';

import {
  ALL_LETTERS_AND_DIGITS_REGEXP,
  DEFAULT_GROUP_COLOR,
  EGroupColors,
} from 'consts';
import { LocalizationContext } from 'localization';
import notification from 'services/notificationService';

import s from './ManageHostGroupModal.module.scss';
import {
  CREATE_HOST_GROUP,
  ICreateHostGroupResponse,
  IUpdateHostGroupResponse,
  UPDATE_HOST_GROUP,
} from './schema';

const COLORS: Array<EGroupColors> = [
  EGroupColors.BLUE,
  EGroupColors.VIOLET,
  EGroupColors.PINK,
  EGroupColors.GREEN,
  EGroupColors.WAVE,
];

const PRIORITY = [
  { title: '1', value: 1 },
  { title: '2', value: 2 },
  { title: '3', value: 3 },
  { title: '4', value: 4 },
  { title: '5', value: 5 },
];

interface IFieldValues {
  title: string;
  color: string;
  priority: number | null;
}

interface IManageHostGroupModalProps {
  groupId?: number | null;
  closeModal: () => void;
  onSuccess: CallableFunction;
}
export const ManageHostGroupModal: FC<IManageHostGroupModalProps> = ({
  groupId: id,
  closeModal,
  onSuccess,
}) => {
  const { t } = useContext(LocalizationContext);

  const apolloClient = useApolloClient();

  const group = apolloClient.cache.readFragment<{
    title: string;
    priority: number;
    customization?: { color?: string };
  }>({
    id: apolloClient.cache.identify({
      __typename: 'HostGroupOverview',
      id,
    }),
    fragment: gql`
      fragment group on HostGroupOverview {
        title
        customization {
          color
        }
        priority
      }
    `,
  });

  const [updateHostGroup, { loading: updateLoading }] =
    useMutation<IUpdateHostGroupResponse>(UPDATE_HOST_GROUP, {
      onCompleted: ({ updateHostGroup: { title } }) => {
        onSuccess();
        notification.info({
          t,
          leftDescription: title,
          title: t('group.updated'),
          type: 'success',
          dismiss: 5,
        });
        closeModal();
      },
    });
  const [createHostGroup, { loading: createLoading }] =
    useMutation<ICreateHostGroupResponse>(CREATE_HOST_GROUP, {
      onCompleted: ({ createHostGroup: { title } }) => {
        onSuccess();
        notification.info({
          t,
          leftDescription: title,
          title: t('group.created'),
          type: 'success',
          dismiss: 5,
        });
        closeModal();
      },
    });

  const { handleSubmit, control } = useForm<IFieldValues>({
    defaultValues: {
      title: group?.title ?? '',
      priority:
        group?.priority === 0 ? null : group?.priority ? group?.priority : 3,
      color: group?.customization?.color || DEFAULT_GROUP_COLOR,
    },
    mode: 'onChange',
  });

  return (
    <Modal onClose={closeModal} disableOutsideClose>
      <form
        className={s.root}
        onSubmit={handleSubmit(({ title, color, priority }) => {
          return id
            ? updateHostGroup({
                variables: {
                  id,
                  input: { title, priority, customization: { color } },
                },
              }).catch(console.error)
            : createHostGroup({
                variables: {
                  input: { title, priority, customization: { color } },
                },
              }).catch(console.error);
        })}
      >
        <span className={s.header}>
          {t(id ? 'group.edit' : 'group.create')}
        </span>
        <div className={s.main}>
          <Controller
            control={control}
            name="title"
            rules={{
              required: t('errors.required'),
              minLength: {
                value: 3,
                message: t('errors.minLength', 3),
              },
              maxLength: {
                value: 12,
                message: t('errors.maxLength', 12),
              },
              validate: (value) =>
                ALL_LETTERS_AND_DIGITS_REGEXP.test(value)
                  ? undefined
                  : t('errors.notOnlySpecial'),
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <Input
                  title={t('title')}
                  value={value}
                  onChange={onChange}
                  placeholder={t('title.placeholder')}
                  error={error?.message}
                  maxLength={12}
                />
              );
            }}
          />
          <div className={s.select_block}>
            <Controller
              control={control}
              name="priority"
              rules={{
                required: t('errors.required'),
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <Select
                    {...field}
                    name="priority"
                    options={PRIORITY}
                    title={t('priority')}
                    error={error?.message}
                  />
                );
              }}
            />
            <span className={s.text}>{t('group.create.text')}</span>
          </div>
          <Controller
            control={control}
            name="color"
            render={({ field: { onChange, value } }) => {
              return (
                <div className={s.color}>
                  {COLORS.map((color) => (
                    <div
                      key={`color-${color}`}
                      className={s.outline}
                      style={{
                        borderColor:
                          value === color ? color : 'var(--border-gray-20)',
                      }}
                      onClick={() => onChange(color)}
                    >
                      <div
                        style={{ backgroundColor: color }}
                        className={s.inner}
                      >
                        {value === color && <CheckIcon />}
                      </div>
                    </div>
                  ))}
                </div>
              );
            }}
          />
        </div>
        <div className={s.footer}>
          <Button
            type="submit"
            fullWidth
            isLoading={updateLoading || createLoading}
          >
            {t(id ? 'save' : 'create')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
